import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  SimpleGrid,
  VStack,
  Icon,
  Spinner,
  Divider,
  useToast,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Edit05Icon, UploadCloud02Icon } from '../../assets/icons'
import LogoIcon from '../../assets/wrk_logo.png'
import { getCodeByCountryName, getCountryNameByCode } from '../countries/PhoneDial'
import { getOrganization, updateOrganization } from '../../api/settings/organization'
import EditableField from '../workforce/people/peopleDetails/EditableField'
import { Employee, Address, UpdateOrganizationPayload } from '../../types/settings/organizationTypes'
import { fetchShortEmployeeList } from '../../api/workforce/workforce'
import { phoneNumbers } from '../countries/PhoneDial'
import handleToast from '../HandleToast'

interface OrganizationFormData {
  logo: File | string | null
  legalName: string
  displayName: string
  url: string
  email: string
  phoneNumber: string
  emailDomain: string
  pilotStart: string
  licenseStart: string
  postalAddress: Address
  visitingAddress: Address
  vatNumber: string
  invoiceAddress: Address
  primaryContact: Employee | null
  secondaryContact: Employee | null
  tertiaryContact: Employee | null
  invoiceContact: Employee | null
}

/** Parse DD/MM/YYYY from EditableField date save to YYYY-MM-DD for API */
function ddMmYyyyToIso(s: string): string {
  const parts = s.split('/')
  if (parts.length !== 3) return ''
  const [d, m, y] = parts
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

const countryOptions = phoneNumbers.map((c) => ({ key: c.code.toLowerCase(), name: c.name }))

const OrganizationSettings: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const logoInputRef = useRef<HTMLInputElement>(null)

  const { data: organization, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: getOrganization,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOrganizationPayload }) => updateOrganization(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      handleToast(toast, true, t('common.states.success'), t('settings.organization.updateSuccess'))
    },
    onError: (error: Error) => {
      handleToast(toast, false, t('common.states.error'), error?.message || t('settings.organization.updateError'))
    },
  })

  const buildPayload = ({ includeLogo = false }: { includeLogo?: boolean } = {}): UpdateOrganizationPayload => {
    const payload: UpdateOrganizationPayload = {
      legal_name: formData.legalName,
      display_name: formData.displayName,
      url: formData.url,
      email: formData.email,
      phone_number: formData.phoneNumber,
      email_domain: formData.emailDomain || undefined,
      pilot_start: formData.pilotStart || null,
      license_start: formData.licenseStart || null,
      postal_address: formData.postalAddress,
      visiting_address: formData.visitingAddress,
      vat_number: formData.vatNumber,
      invoice_address: formData.invoiceAddress,
      primary_contact: formData.primaryContact?.id ?? null,
      secondary_contact: formData.secondaryContact?.id ?? null,
      tertiary_contact: formData.tertiaryContact?.id ?? null,
      invoice_contact: formData.invoiceContact?.id ?? null,
    }

    if (includeLogo) {
      payload.logo = formData.logo
    }

    return payload
  }

  const handleSave = () => {
    if (!organization?.id) return
    updateMutation.mutate({ id: organization.id, payload: buildPayload() })
  }

  /** When a contact is selected but has no email/phone, show N/A instead of "Select employee". */
  const contactEmailDisplay = (contact: Employee | null) =>
    contact ? (contact.work_email?.trim() || t('common.labels.na')) : ''
  const contactPhoneDisplay = (contact: Employee | null) =>
    contact ? (contact.phone_number?.trim() || t('common.labels.na')) : ''

  const saveContactAndPersist = (
    contactKey: 'primaryContact' | 'secondaryContact' | 'tertiaryContact',
    user: { id: string; first_name: string; last_name: string; profile_picture?: string },
  ) => {
    const newContact =
      employeeList.find((e) => e.id === user.id) ?? ({
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        profile_picture: user.profile_picture,
        work_email: '',
        phone_number: '',
      })
    setFormData((prev) => ({ ...prev, [contactKey]: newContact }))
    if (!organization?.id) return
    const payload = buildPayload()
    const key = contactKey === 'primaryContact' ? 'primary_contact' : contactKey === 'secondaryContact' ? 'secondary_contact' : 'tertiary_contact'
    updateMutation.mutate({
      id: organization.id,
      payload: { ...payload, [key]: user.id },
    })
  }

  const employeeQuery = useInfiniteQuery({
    queryKey: ['employees', ''],
    queryFn: ({ pageParam = 1 }) => fetchShortEmployeeList({ search: '', page: pageParam, pageSize: 10 }),
    getNextPageParam: (lastPage: any, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
    initialPageParam: 1,
  })

  const employeeList: Employee[] = (employeeQuery.data?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : []
  ) ?? []).filter((employee: Employee | undefined): employee is Employee => Boolean(employee))

  const [formData, setFormData] = useState<OrganizationFormData>({
    logo: null,
    legalName: '',
    displayName: '',
    url: '',
    email: '',
    phoneNumber: '',
    emailDomain: '',
    pilotStart: '',
    licenseStart: '',
    postalAddress: {
      address1: '',
      address2: '',
      country: '',
      city: '',
      zipCode: '',
    } as Address,
    visitingAddress: {
      address1: '',
      address2: '',
      country: '',
      city: '',
      zipCode: '',
    } as Address,
    vatNumber: '',
    invoiceAddress: {
      address1: '',
      address2: '',
      country: '',
      city: '',
      zipCode: '',
    } as Address,
    primaryContact: null as Employee | null,
    secondaryContact: null as Employee | null,
    tertiaryContact: null as Employee | null,
    invoiceContact: null as Employee | null,
  })
  const [initialFormData, setInitialFormData] = useState<OrganizationFormData | null>(null)

  const contactId = (contact: Employee | null) => contact?.id ?? null

  useEffect(() => {
    if (organization) {
      const phoneNumber = organization.phone_number || ''
      const nextFormData: OrganizationFormData = {
        logo: organization.logo,
        legalName: organization.legal_name || '',
        displayName: organization.display_name || '',
        url: organization.url || '',
        email: organization.email || '',
        phoneNumber,
        emailDomain: organization.email_domain || '',
        pilotStart: organization.pilot_start ? organization.pilot_start.split('T')[0] : '',
        licenseStart: organization.license_start ? organization.license_start.split('T')[0] : '',
        postalAddress: organization.postal_address || {
          address1: '',
          address2: '',
          country: '',
          city: '',
          zipCode: '',
        },
        visitingAddress: organization.visiting_address || {
          address1: '',
          address2: '',
          country: '',
          city: '',
          zipCode: '',
        },
        vatNumber: organization.vat_number || '',
        invoiceAddress: organization.invoice_address || {
          address1: '',
          address2: '',
          country: '',
          city: '',
          zipCode: '',
        },
        primaryContact: organization.primary_contact,
        secondaryContact: organization.secondary_contact,
        tertiaryContact: organization.tertiary_contact,
        invoiceContact: organization.invoice_contact,
      }
      setFormData(nextFormData)
      setInitialFormData(nextFormData)
    }
  }, [organization])

  const organizationInfoDirty = useMemo(() => {
    if (!initialFormData) return false
    return JSON.stringify({
      legalName: formData.legalName,
      displayName: formData.displayName,
      url: formData.url,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      pilotStart: formData.pilotStart,
      licenseStart: formData.licenseStart,
    }) !==
      JSON.stringify({
        legalName: initialFormData.legalName,
        displayName: initialFormData.displayName,
        url: initialFormData.url,
        email: initialFormData.email,
        phoneNumber: initialFormData.phoneNumber,
        pilotStart: initialFormData.pilotStart,
        licenseStart: initialFormData.licenseStart,
      })
  }, [formData, initialFormData])

  const addressesDirty = useMemo(() => {
    if (!initialFormData) return false
    return JSON.stringify({
      postalAddress: formData.postalAddress,
      visitingAddress: formData.visitingAddress,
    }) !==
      JSON.stringify({
        postalAddress: initialFormData.postalAddress,
        visitingAddress: initialFormData.visitingAddress,
      })
  }, [formData.postalAddress, formData.visitingAddress, initialFormData])

  const invoicingDirty = useMemo(() => {
    if (!initialFormData) return false
    return JSON.stringify({
      vatNumber: formData.vatNumber,
      invoiceAddress: formData.invoiceAddress,
      invoiceContactId: contactId(formData.invoiceContact),
    }) !==
      JSON.stringify({
        vatNumber: initialFormData.vatNumber,
        invoiceAddress: initialFormData.invoiceAddress,
        invoiceContactId: contactId(initialFormData.invoiceContact),
      })
  }, [formData.vatNumber, formData.invoiceAddress, formData.invoiceContact, initialFormData])

  const MAX_LOGO_SIZE_MB = 5
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      handleToast(toast, false, t('common.states.error'), t('settings.organization.invalidImageType'))
      event.target.value = ''
      return
    }

    if (file.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      handleToast(toast, false, t('common.states.error'), t('settings.organization.fileTooLarge'))
      event.target.value = ''
      return
    }

    if (organization?.id) {
      setFormData((prev) => ({ ...prev, logo: file }))
      updateMutation.mutate({
        id: organization.id,
        payload: { logo: file },
      })
      event.target.value = ''
    } else {
      setFormData((prev) => ({ ...prev, logo: file }))
    }
  }

  const handleLogoRemove = () => {
    if (!organization?.id) return
    setFormData((prev) => ({ ...prev, logo: null }))
    updateMutation.mutate({
      id: organization.id,
      payload: { logo: null },
    })
    if (logoInputRef.current) logoInputRef.current.value = ''
  }



  const getLogoSrc = (): string => {
    if (formData.logo instanceof File) {
      return URL.createObjectURL(formData.logo)
    }
    if (formData.logo) {
      return formData.logo
    }
    return LogoIcon
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    )
  }

  const sectionTitleProps = {
    textStyle: 'h-s' as const,
    color: 'solid.900' as const,
    mb: 4,
    pl: 3,
    borderLeft: '3px solid',
    borderLeftColor: 'aqua.100' as const,
  }

  return (
    <>
      <VStack align="stretch" spacing={0} w="100%">
        <Box w="100%" mb="12px">
          <SimpleGrid columns={3} spacing={6} gap={6}>
            {/* Logo */}
            <Box
              bg="white"
              borderRadius="12px"
              p={7}
              minH="280px"
              minW="200px"
              boxShadow="0px 1px 2px rgba(93, 94, 203, 0.04), 0px 2px 8px rgba(93, 94, 203, 0.06)"
              display="flex"
              flexDirection="column"
            >
              <Text {...sectionTitleProps}>
                {t('settings.organization.logo')}
              </Text>
              <Flex flex="1 1 auto" minH="0" align="center" justify="center" mb={3}>
                <Box
                  borderRadius="full"
                  bg="white"
                  borderWidth="1px"
                  borderColor="primary.200"
                  boxShadow="0px 4px 16px rgba(93, 94, 203, 0.35)"
                  boxSize="220px"
                  minW="220px"
                  minH="220px"
                  p={formData.logo ? 0 : '10%'}
                  overflow="hidden"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    as="img"
                    src={getLogoSrc()}
                    alt="Organization logo"
                    objectFit={formData.logo ? 'cover' : 'contain'}
                    w="100%"
                    h="100%"
                  />
                </Box>
              </Flex>
              <Flex flex="0 0 auto" gap={2} flexWrap="wrap" justify="space-between" w="100%">
                <Button variant="mono" size="sm" as="label" htmlFor="logo-upload" cursor="pointer">
                  <Icon width="16px" height="16px" viewBox="0 0 24 24" fill="none" color="white" mr="6px">
                    <UploadCloud02Icon />
                  </Icon>
                  {t('settings.organization.uploadLogo')}
                </Button>
                <Input
                  id="logo-upload"
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  display="none"
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogoRemove}
                  isDisabled={!formData.logo}
                  isLoading={updateMutation.isPending}
                >
                  {t('settings.organization.remove')}
                </Button>
              </Flex>
            </Box>

            {/* Organization Information */}
            <Box
              bg="white"
              borderRadius="12px"
              p={7}
              gridColumn="span 2"
              w="100%"
              minW={0}
              boxShadow="0px 1px 2px rgba(93, 94, 203, 0.04), 0px 2px 8px rgba(93, 94, 203, 0.06)"
            >
              <Flex justify="space-between" align="center" mb={4}>
                <Text {...sectionTitleProps} mb={0}>
                  {t('settings.organization.organizationInfo')}
                </Text>
                {organizationInfoDirty && (
                  <Button variant="primary" gap={2} onClick={handleSave} isLoading={updateMutation.isPending}>
                    <Icon width="16px" height="16px">
                      <Edit05Icon />
                    </Icon>
                    <Text textStyle="l-s">{t('common.actions.save')}</Text>
                  </Button>
                )}
              </Flex>
          <SimpleGrid columns={3} spacing={4} rowGap={8} w="100%" minW={0} templateColumns="1fr 1fr 1fr">
              <Box>
                <EditableField
                  label={t('settings.organization.legalName')}
                  value={formData.legalName}
                  onSave={(v) => setFormData({ ...formData, legalName: v })}
                  placeholder={t('settings.organization.legalNamePlaceholder')}
                  width="100%"
                />
              </Box>
              <Box>
                <EditableField
                  label={t('settings.organization.businessName')}
                  value={formData.displayName}
                  onSave={(v) => setFormData({ ...formData, displayName: v })}
                  placeholder={t('settings.organization.businessNamePlaceholder')}
                  width="100%"
                />
              </Box>
              <Box />

              <Box>
                <EditableField
                  label={t('settings.organization.url')}
                  value={formData.url}
                  onSave={(v) => setFormData({ ...formData, url: v })}
                  placeholder="https://example.com"
                  width="100%"
                />
              </Box>
              <Box>
                <EditableField
                  label={t('settings.organization.email')}
                  value={formData.email}
                  onSave={(v) => setFormData({ ...formData, email: v })}
                  placeholder={t('settings.organization.emailPlaceholder')}
                  width="100%"
                />
              </Box>
              <Box />

              <Box>
                <EditableField
                  label={t('settings.organization.phoneNumber')}
                  value={formData.phoneNumber}
                  onSave={(v) => setFormData({ ...formData, phoneNumber: v })}
                  isPhoneNumber
                  phonePlaceholder={t('settings.organization.phonePlaceholder')}
                  width="100%"
                />
              </Box>
              <Box>
                <EditableField
                  label={t('settings.organization.startDatePilot')}
                  value={formData.pilotStart}
                  onSave={(v) => setFormData({ ...formData, pilotStart: ddMmYyyyToIso(v) || v })}
                  isDate
                  width="100%"
                />
              </Box>
              <Box>
                <EditableField
                  label={t('settings.organization.startDateLicense')}
                  value={formData.licenseStart}
                  onSave={(v) => setFormData({ ...formData, licenseStart: ddMmYyyyToIso(v) || v })}
                  isDate
                  width="100%"
                />
              </Box>
            </SimpleGrid>
        </Box>

        {/* Contacts */}
        <Box
          bg="white"
          borderRadius="12px"
          p={7}
          gridRow="span 2"
          boxShadow="0px 1px 2px rgba(93, 94, 203, 0.04), 0px 2px 8px rgba(93, 94, 203, 0.06)"
        >
          <Text {...sectionTitleProps} mb={5}>
            {t('settings.organization.contacts')}
          </Text>
          <VStack align="stretch" spacing={6}>
            {/* 1st Contact */}
            <Box>
              <Text textStyle="h-xs" fontWeight="600" color="solid.700" mb={4}>
                {t('settings.organization.firstContact')}
              </Text>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <EditableField
                    label={t('settings.organization.contactName')}
                    value={formData.primaryContact?.id ?? ''}
                    onSave={() => {}}
                    isEmployee
                    managerDetails={formData.primaryContact ? { id: formData.primaryContact.id, first_name: formData.primaryContact.first_name, last_name: formData.primaryContact.last_name, profile_picture: formData.primaryContact.profile_picture || '' } : undefined}
                    onEmployeeSave={(user) => saveContactAndPersist('primaryContact', user)}
                    employeePlaceholder={t('settings.organization.selectEmployee')}
                    width="100%"
                  />
                </Box>
                <EditableField
                  label={t('settings.organization.contactEmail')}
                  value={contactEmailDisplay(formData.primaryContact)}
                  onSave={() => {}}
                  hideEditableIndicator
                  isDisabled
                  placeholder={t('settings.organization.selectEmployee')}
                  width="100%"
                />
                <EditableField
                  label={t('settings.organization.contactPhone')}
                  value={contactPhoneDisplay(formData.primaryContact)}
                  onSave={() => {}}
                  hideEditableIndicator
                  isDisabled
                  isPhoneNumber
                  placeholder={t('settings.organization.selectEmployee')}
                  width="100%"
                />
              </VStack>
            </Box>

            <Divider orientation="horizontal" borderColor="solid.200" borderWidth="1px" my={4} />

            {/* 2nd Contact */}
            <Box>
              <Text textStyle="h-xs" fontWeight="600" color="solid.700" mb={4}>
                {t('settings.organization.secondContact')}
              </Text>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <EditableField
                    label={t('settings.organization.contactName')}
                    value={formData.secondaryContact?.id ?? ''}
                    onSave={() => {}}
                    isEmployee
                    managerDetails={formData.secondaryContact ? { id: formData.secondaryContact.id, first_name: formData.secondaryContact.first_name, last_name: formData.secondaryContact.last_name, profile_picture: formData.secondaryContact.profile_picture || '' } : undefined}
                    onEmployeeSave={(user) => saveContactAndPersist('secondaryContact', user)}
                    employeePlaceholder={t('settings.organization.selectEmployee')}
                    width="100%"
                  />
                </Box>
                <EditableField
                  label={t('settings.organization.contactEmail')}
                  value={contactEmailDisplay(formData.secondaryContact)}
                  onSave={() => {}}
                  hideEditableIndicator
                  isDisabled
                  placeholder={t('settings.organization.selectEmployee')}
                  width="100%"
                />
                <EditableField
                  label={t('settings.organization.contactPhone')}
                  value={contactPhoneDisplay(formData.secondaryContact)}
                  onSave={() => {}}
                  hideEditableIndicator
                  isDisabled
                  isPhoneNumber
                  placeholder={t('settings.organization.selectEmployee')}
                  width="100%"
                />
              </VStack>
            </Box>

            <Divider orientation="horizontal" borderColor="solid.200" borderWidth="1px" my={4} />

            {/* 3rd Contact */}
            <Box>
              <Text textStyle="h-xs" fontWeight="600" color="solid.700" mb={4}>
                {t('settings.organization.thirdContact')}
              </Text>
              <VStack align="stretch" spacing={4}>
                <Box>
                  <EditableField
                    label={t('settings.organization.contactName')}
                    value={formData.tertiaryContact?.id ?? ''}
                    onSave={() => {}}
                    isEmployee
                    managerDetails={formData.tertiaryContact ? { id: formData.tertiaryContact.id, first_name: formData.tertiaryContact.first_name, last_name: formData.tertiaryContact.last_name, profile_picture: formData.tertiaryContact.profile_picture || '' } : undefined}
                    onEmployeeSave={(user) => saveContactAndPersist('tertiaryContact', user)}
                    employeePlaceholder={t('settings.organization.selectEmployee')}
                    width="100%"
                  />
                </Box>
                <EditableField
                  label={t('settings.organization.contactEmail')}
                  value={contactEmailDisplay(formData.tertiaryContact)}
                  onSave={() => {}}
                  hideEditableIndicator
                  isDisabled
                  placeholder={t('settings.organization.selectEmployee')}
                  width="100%"
                />
                <EditableField
                  label={t('settings.organization.contactPhone')}
                  value={contactPhoneDisplay(formData.tertiaryContact)}
                  onSave={() => {}}
                  hideEditableIndicator
                  isDisabled
                  isPhoneNumber
                  placeholder={t('settings.organization.selectEmployee')}
                  width="100%"
                />
              </VStack>
            </Box>
          </VStack>
        </Box>

        {/* Addresses */}
        <Box
          bg="white"
          borderRadius="12px"
          p={7}
          gridColumn="span 2"
          boxShadow="0px 1px 2px rgba(93, 94, 203, 0.04), 0px 2px 8px rgba(93, 94, 203, 0.06)"
        >
          <Flex justify="space-between" align="center" mb={5}>
            <Text {...sectionTitleProps} mb={0}>
              {t('settings.organization.addresses')}
            </Text>
            {addressesDirty && (
              <Button variant="primary" gap={2} onClick={handleSave} isLoading={updateMutation.isPending}>
                <Icon width="16px" height="16px">
                  <Edit05Icon />
                </Icon>
                <Text textStyle="l-s">{t('common.actions.save')}</Text>
              </Button>
            )}
          </Flex>
          <Text textStyle="h-xs" fontWeight="600" color="solid.700" mb={4}>
            {t('settings.organization.postalAddress')}
          </Text>
          <SimpleGrid columns={2} spacing={4} mb={6}>
            <Box>
              <EditableField
                label={t('settings.organization.addressLine1')}
                value={formData.postalAddress.address1}
                onSave={(v) => setFormData({ ...formData, postalAddress: { ...formData.postalAddress, address1: v } })}
                placeholder={t('settings.organization.addressPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.addressLine2')}
                value={formData.postalAddress.address2}
                onSave={(v) => setFormData({ ...formData, postalAddress: { ...formData.postalAddress, address2: v } })}
                placeholder={t('settings.organization.addressPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.city')}
                value={formData.postalAddress.city}
                onSave={(v) => setFormData({ ...formData, postalAddress: { ...formData.postalAddress, city: v } })}
                placeholder={t('settings.organization.cityPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.zipCode')}
                value={formData.postalAddress.zipCode}
                onSave={(v) => setFormData({ ...formData, postalAddress: { ...formData.postalAddress, zipCode: v } })}
                placeholder={t('settings.organization.zipCodePlaceholder')}
                width="100%"
              />
            </Box>
            <Box gridColumn="span 2">
              <EditableField
                label={t('settings.organization.country')}
                value={getCountryNameByCode((formData.postalAddress.country || '').toUpperCase()) || formData.postalAddress.country || ''}
                onSave={(name) => setFormData({ ...formData, postalAddress: { ...formData.postalAddress, country: (getCodeByCountryName(name) || name || '').toUpperCase() } })}
                isCountrySelect
                countryOptions={countryOptions}
                placeholder={t('settings.organization.countryPlaceholder')}
                width="100%"
              />
            </Box>
          </SimpleGrid>

          <Divider orientation="horizontal" borderColor="solid.200" borderWidth="1px" my={4} />

          <Text textStyle="h-xs" fontWeight="600" color="solid.700" mb={4}>
            {t('settings.organization.visitingAddress')}
          </Text>
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <EditableField
                label={t('settings.organization.addressLine1')}
                value={formData.visitingAddress.address1}
                onSave={(v) => setFormData({ ...formData, visitingAddress: { ...formData.visitingAddress, address1: v } })}
                placeholder={t('settings.organization.addressPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.addressLine2')}
                value={formData.visitingAddress.address2}
                onSave={(v) => setFormData({ ...formData, visitingAddress: { ...formData.visitingAddress, address2: v } })}
                placeholder={t('settings.organization.addressPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.city')}
                value={formData.visitingAddress.city}
                onSave={(v) => setFormData({ ...formData, visitingAddress: { ...formData.visitingAddress, city: v } })}
                placeholder={t('settings.organization.cityPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.zipCode')}
                value={formData.visitingAddress.zipCode}
                onSave={(v) => setFormData({ ...formData, visitingAddress: { ...formData.visitingAddress, zipCode: v } })}
                placeholder={t('settings.organization.zipCodePlaceholder')}
                width="100%"
              />
            </Box>
            <Box gridColumn="span 2">
              <EditableField
                label={t('settings.organization.country')}
                value={getCountryNameByCode((formData.visitingAddress.country || '').toUpperCase()) || formData.visitingAddress.country || ''}
                onSave={(name) => setFormData({ ...formData, visitingAddress: { ...formData.visitingAddress, country: (getCodeByCountryName(name) || name || '').toUpperCase() } })}
                isCountrySelect
                countryOptions={countryOptions}
                placeholder={t('settings.organization.countryPlaceholder')}
                width="100%"
              />
            </Box>
          </SimpleGrid>
        </Box>

        {/* Invoicing Information */}
        <Box
          bg="white"
          borderRadius="12px"
          p={7}
          gridColumn="span 2"
          boxShadow="0px 1px 2px rgba(93, 94, 203, 0.04), 0px 2px 8px rgba(93, 94, 203, 0.06)"
        >
          <Flex justify="space-between" align="center" mb={5}>
            <Text {...sectionTitleProps} mb={0}>
              {t('settings.organization.invoicingInfo')}
            </Text>
            {invoicingDirty && (
              <Button variant="primary" gap={2} onClick={handleSave} isLoading={updateMutation.isPending}>
                <Icon width="16px" height="16px">
                  <Edit05Icon />
                </Icon>
                <Text textStyle="l-s">{t('common.actions.save')}</Text>
              </Button>
            )}
          </Flex>
          <SimpleGrid columns={3} spacing={4} rowGap={8} >
            <Box>
              <EditableField
                label={t('settings.organization.vat')}
                value={formData.vatNumber}
                onSave={(v) => setFormData({ ...formData, vatNumber: v })}
                placeholder={t('settings.organization.vatPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.addressLine1')}
                value={formData.invoiceAddress.address1}
                onSave={(v) => setFormData({ ...formData, invoiceAddress: { ...formData.invoiceAddress, address1: v } })}
                placeholder={t('settings.organization.addressPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.addressLine2')}
                value={formData.invoiceAddress.address2}
                onSave={(v) => setFormData({ ...formData, invoiceAddress: { ...formData.invoiceAddress, address2: v } })}
                placeholder={t('settings.organization.addressPlaceholder')}
                width="100%"
              />
            </Box>
            <Box gridColumn="1">
              <EditableField
                label={t('settings.organization.country')}
                value={getCountryNameByCode((formData.invoiceAddress.country || '').toUpperCase()) || formData.invoiceAddress.country || ''}
                onSave={(name) => setFormData({ ...formData, invoiceAddress: { ...formData.invoiceAddress, country: (getCodeByCountryName(name) || name || '').toUpperCase() } })}
                isCountrySelect
                countryOptions={countryOptions}
                placeholder={t('settings.organization.countryPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.city')}
                value={formData.invoiceAddress.city}
                onSave={(v) => setFormData({ ...formData, invoiceAddress: { ...formData.invoiceAddress, city: v } })}
                placeholder={t('settings.organization.cityPlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.zipCode')}
                value={formData.invoiceAddress.zipCode}
                onSave={(v) => setFormData({ ...formData, invoiceAddress: { ...formData.invoiceAddress, zipCode: v } })}
                placeholder={t('settings.organization.zipCodePlaceholder')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.invoiceContactName')}
                value={formData.invoiceContact?.id ?? ''}
                onSave={() => {}}
                isEmployee
                managerDetails={formData.invoiceContact ? { id: formData.invoiceContact.id, first_name: formData.invoiceContact.first_name, last_name: formData.invoiceContact.last_name, profile_picture: formData.invoiceContact.profile_picture || '' } : undefined}
                onEmployeeSave={(user) =>
                  setFormData({
                    ...formData,
                    invoiceContact: employeeList.find((e) => e.id === user.id) ?? {
                      id: user.id,
                      first_name: user.first_name,
                      last_name: user.last_name,
                      profile_picture: user.profile_picture,
                      work_email: '',
                      phone_number: '',
                    },
                  })
                }
                employeePlaceholder={t('settings.organization.selectEmployee')}
                width="100%"
              />
            </Box>
            <Box>
              <EditableField
                label={t('settings.organization.invoiceContactEmail')}
                value={formData.invoiceContact?.work_email ?? ''}
                onSave={() => {}}
                hideEditableIndicator
                isDisabled
                placeholder={t('settings.organization.selectEmployee')}
                width="100%"
              />
            </Box>
          </SimpleGrid>
        </Box>
        </SimpleGrid>
        </Box>
      </VStack>
    </>
  )
}

export default OrganizationSettings
